import { useQuery } from '@apollo/client';
import { Create } from '@mui/icons-material';
import { Button, Grid, Icon, Paper, Pagination } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import ROLES_QUERY from 'gql/queries/PermissionProvider/getRoles.gql';

import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { RoleItem } from 'app/components/RichList/items/PermissionProvider/RoleItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { RoleModal } from './RoleModal';

export function RoleList() {
  const roleNameState = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rolesQuery = useQuery<any, any>(ROLES_QUERY, { fetchPolicy: 'network-only' });
  const { refetch } = rolesQuery;

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    refetch({ offset: offset });
  };

  return (
    <GQLGuard
      query={rolesQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {data => (
        <>
          <ErrorBoundary boundaryTag="roleDetailBoundary">
            <Modal
              open={createModalOpen}
              onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') return;
                setCreateModalOpen(false);
              }}
              headline={t(translations.pages.managePermissionsSettings.roleCreation())}
              onCancel={() => {
                setCreateModalOpen(false);
              }}
              disabledCancel={disabledCancel}
            >
              <RoleModal
                onCancel={() => setCreateModalOpen(false)}
                closeHandler={() => {
                  setCreateModalOpen(false);
                }}
                setDisabledCancel={setDisabledCancel}
              />
            </Modal>
          </ErrorBoundary>
          <RichList
            controls={
              <>
                <Button
                  startIcon={<Create />}
                  variant="contained"
                  className="manage-companies__actions--download"
                  onClick={() => setCreateModalOpen(true)}
                >
                  {t(translations.pages.managePermissionsSettings.createRole())}
                </Button>
              </>
            }
            onFilterChange={refetch}
            filters={[
              {
                field: 'name',
                label: t(translations.pages.managePermissionsSettings.role()),
                stateHandler: roleNameState
              }
            ]}
            staticHeading={
              <Grid
                container
                className="permission-settings__list-heading"
                component={Paper}
                square
              >
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.common.id())}
                </Grid>
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.icon())}
                </Grid>
                <Grid item md={3} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.roleName())}
                </Grid>
                <Grid item md={6} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.entitlements())}
                </Grid>
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.actions())}
                </Grid>
              </Grid>
            }
          >
            {data.roles.length === 0 ? (
              <NoItems
                icon={<Icon className="fas fa-info-circle" />}
                message={t(translations.pages.managePermissionsSettings.noItemFound())}
              />
            ) : (
              <Grid container className="permission-settings__list" component={Paper}>
                {data.roles.map(role => (
                  <ErrorBoundary key={role.id} boundaryTag="entitlementItemBoundary">
                    <RoleItem role={role} key={role.id} />
                  </ErrorBoundary>
                ))}
              </Grid>
            )}
          </RichList>
          {Math.floor(data.rolesCount / 10) > 0 && (
            <Grid container className="permission-settings__pagination">
              <Pagination
                count={Math.ceil(data.rolesCount / 10)}
                onChange={handlePagination}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
                size="medium"
                color="primary"
                siblingCount={2}
                boundaryCount={2}
              />
            </Grid>
          )}
        </>
      )}
    </GQLGuard>
  );
}
